/**
 * Maps Module
 * Handles Leaflet + Stadia Maps integration
 * - Main map on detail page with office marker
 * - Listing page with clustered office markers
 * - Transport points overlay (toggle)
 * - Mobile full-screen map
 * - Street View iframe dialog
 * - Fullscreen map dialog (listing)
 */

import { openDialog, loadIframeInDialog } from './dialogs.js';

let map = null;
let mobileMap = null;
let fullscreenMap = null;
let transportLayerGroup = null;
let transportVisible = false;
let originalView = null;
let initialListingView = null;
let officeClusterGroup = null;
const TRANSPORT_SPRITE_PATH = '/img/sprite-transport.svg?v=20260312';

export function createOfficeIcon() {
    return L.icon({
        iconUrl: '/img/map-marker.svg?v=2',
        iconSize: [36, 62],
        iconAnchor: [18, 62],
        popupAnchor: [0, -58]
    });
}

export function createTileLayer(options = {}) {
    const stadiaKey = window.STADIA_KEY || '';
    const layer = L.tileLayer(`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${stadiaKey}`, {
        maxZoom: 20,
        ...options
    });

    // Retry failed tiles after a short delay (handles race conditions on hard refresh)
    layer.on('tileerror', function(e) {
        const tile = e.tile;
        const src = tile.src;
        if (!tile._retryCount) tile._retryCount = 0;
        if (tile._retryCount < 2) {
            tile._retryCount++;
            setTimeout(() => { tile.src = ''; tile.src = src; }, 500 * tile._retryCount);
        }
    });

    return layer;
}

function createClusterGroup() {
    return L.markerClusterGroup({
        maxClusterRadius: 50,
        iconCreateFunction: function(cluster) {
            const count = cluster.getChildCount();
            return L.divIcon({
                html: `<span class="cluster-count">${count}</span>`,
                className: 'listing-cluster-icon',
                iconSize: [58, 53],
                iconAnchor: [29, 53]
            });
        },
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true
    });
}

function addCardMarkersToGroup(clusterGroup, cards, officeIcon) {
    const bounds = L.latLngBounds();
    cards.forEach(card => {
        const cLat = parseFloat(card.dataset.officeLat);
        const cLng = parseFloat(card.dataset.officeLng);
        const name = card.dataset.officeName || '';
        if (isNaN(cLat) || isNaN(cLng)) return;

        const popupHtml = buildCardPopup(card, name);
        const marker = L.marker([cLat, cLng], { icon: officeIcon })
            .bindPopup(popupHtml, {
                className: 'map-card-popup',
                maxWidth: 300,
                minWidth: 260,
                offset: [0, -8]
            });

        // Pan marker to bottom-centre of map so popup has room above
        marker.on('click', () => {
            if (!map) return;
            map.invalidateSize();
            const px = map.latLngToContainerPoint([cLat, cLng]);
            const size = map.getSize();
            // Centre horizontally, position at ~92% down vertically
            const targetX = size.x / 2;
            const targetY = size.y * 0.92;
            map.panBy([px.x - targetX, px.y - targetY], { animate: true, duration: 0.4 });
        });

        clusterGroup.addLayer(marker);
        bounds.extend([cLat, cLng]);
    });
    return bounds;
}

/** Build rich popup HTML from a listing card's DOM data */
function buildCardPopup(card, name) {
    const img = card.querySelector('.office-card-image');
    const imgSrc = img ? img.src : '';
    const imgAlt = img ? img.alt : name;
    const href = card.dataset.href || '#';

    // Extract price — always build in home-card format: label + large value + compact unit
    let priceHtml = '';
    const priceBlock = card.querySelector('.listing-card-price');
    if (priceBlock) {
        // Try structured .rates first (priceDescription entries)
        const firstRate = priceBlock.querySelector('.rates');
        if (firstRate) {
            const typeEl = firstRate.querySelector('.price-type');
            const valueEl = firstRate.querySelector('.price-value');
            const unitEl = firstRate.querySelector('.price-pmpp');
            const typeText = typeEl ? typeEl.textContent.trim() : '';
            const valueText = valueEl ? valueEl.textContent.trim() : '';
            const unitText = unitEl ? shortenUnit(unitEl.textContent.trim()) : '';
            if (valueText) {
                priceHtml = `<p class="map-card-label"><strong>${typeText}</strong> from</p>
                    <div class="map-card-price">
                        <span class="map-card-price-value">${valueText}</span>
                        <span class="map-card-price-unit">${unitText}</span>
                    </div>`;
            }
        } else {
            // Standard .office-card-label + .office-card-price
            const labelEl = priceBlock.querySelector('.office-card-label');
            const valueEl = priceBlock.querySelector('.office-card-price-value');
            const unitEl = priceBlock.querySelector('.office-card-price-unit');
            const labelHtml = labelEl ? labelEl.innerHTML : '';
            const valueText = valueEl ? valueEl.textContent.trim() : '';
            const unitText = unitEl ? shortenUnit(unitEl.textContent.trim()) : '';
            if (valueText) {
                priceHtml = `<p class="map-card-label">${labelHtml}</p>
                    <div class="map-card-price">
                        <span class="map-card-price-value">${valueText}</span>
                        <span class="map-card-price-unit">${unitText}</span>
                    </div>`;
            }
        }
    }

    return `<div class="map-card">
        <a href="${href}" class="map-card-media">
            ${imgSrc ? `<img src="${imgSrc}" alt="${imgAlt}" loading="lazy">` : ''}
        </a>
        <div class="map-card-body">
            <h3 class="map-card-title">${name}</h3>
            ${priceHtml}
            <a href="${href}" class="map-card-button">View Office Details</a>
        </div>
    </div>`;
}

/** Shorten verbose price units to compact form.
 *  textContent may strip <br> tags leaving no separator, so use flexible matching. */
function shortenUnit(text) {
    if (/per.?person.*per.?month/i.test(text)) return 'pp/pm';
    if (/per.?sq\/ft.*per.?month/i.test(text)) return 'per sq/ft/pm';
    if (/per.?sq\/m.*per.?month/i.test(text)) return 'per sq/m/pm';
    return text.replace(/\s+/g, ' ').trim();
}

export function getMap() { return map; }

/**
 * Show transport markers and hide office markers on the listing map.
 * No-op if transport layer is already visible or layers aren't initialised.
 */
export function showTransportOnMap() {
    if (!map || !transportLayerGroup || transportVisible) return;
    if (officeClusterGroup) map.removeLayer(officeClusterGroup);
    transportLayerGroup.addTo(map);
    transportVisible = true;

    // Update toggle button text to match state
    const btn = document.querySelector('.listing-transport-toggle-btn');
    if (btn) {
        btn.innerHTML = transportBtnLabel('Hide');
    }
}

/**
 * Open the popup on the transport marker nearest to the given coordinates.
 */
export function openTransportPopup(lat, lng) {
    if (!map || !transportLayerGroup) return;
    let closest = null;
    let minDist = Infinity;
    transportLayerGroup.eachLayer(marker => {
        const pos = marker.getLatLng();
        const d = Math.abs(pos.lat - lat) + Math.abs(pos.lng - lng);
        if (d < minDist) { minDist = d; closest = marker; }
    });
    if (closest) closest.openPopup();
}

export function initMaps() {
    const mapContainer = document.getElementById('office-map');
    if (!mapContainer) return;

    // Detect listing vs detail page
    const isListing = document.querySelector('.office-listing');
    if (isListing) {
        initializeListingMap(mapContainer);
        return;
    }

    // Detail page: lazy-load the map when it enters (or nears) the viewport.
    // Saves Stadia tile credits for bots and users who bounce before scrolling.
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                observer.disconnect();
                initDetailMapAndControls(mapContainer);
            }
        }, {
            rootMargin: '200px'   // start loading 200px before it scrolls into view
        });
        observer.observe(mapContainer);
    } else {
        // Fallback for older browsers — load immediately
        initDetailMapAndControls(mapContainer);
    }
}

function initDetailMapAndControls(mapContainer) {
    initializeMainMap(mapContainer);

    // Transport toggle button — only show if there are markers to display
    const transportBtn = document.querySelector('.transport-toggle-btn');
    if (transportBtn) {
        if (transportLayerGroup && transportLayerGroup.getLayers().length > 0) {
            transportBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                toggleTransportLayer(transportBtn);
            });
        } else {
            transportBtn.style.display = 'none';
        }
    }

    // Street View button
    const streetViewBtn = document.querySelector('[data-street-view]');
    if (streetViewBtn) {
        streetViewBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            openStreetView();
        });
    }

    // Mobile map button
    const mobileMapBtn = document.querySelector('.mobile-map-btn');
    if (mobileMapBtn) {
        mobileMapBtn.addEventListener('click', openMobileMap);
    }
}

/**
 * Map activation overlay — prevents accidental map interaction
 * On desktop: hover shows overlay, click activates
 * On touch: first tap shows overlay, second tap activates
 */
function setupMapActivation(container, leafletMap) {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'map-activate-overlay';
    overlay.innerHTML = `
        <svg class="map-activate-icon" aria-hidden="true"><use href="/img/sprite-general.svg#icon-map"></use></svg>
        <span>Click to activate map</span>
    `;
    container.style.position = 'relative';
    container.appendChild(overlay);

    let activated = false;
    let previewing = false;

    function activate() {
        if (activated) return;
        activated = true;
        overlay.classList.add('is-hidden');
        leafletMap.dragging.enable();
        leafletMap.touchZoom.enable();
        leafletMap.doubleClickZoom.enable();
        leafletMap.boxZoom.enable();
        leafletMap.keyboard.enable();
        leafletMap.scrollWheelZoom.enable();
    }

    function deactivate() {
        activated = false;
        previewing = false;
        overlay.classList.remove('is-hidden', 'is-previewing');
        leafletMap.dragging.disable();
        leafletMap.touchZoom.disable();
        leafletMap.doubleClickZoom.disable();
        leafletMap.boxZoom.disable();
        leafletMap.keyboard.disable();
        leafletMap.scrollWheelZoom.disable();
    }

    // Desktop: hover shows overlay highlight, click activates
    if (!isTouch) {
        overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            activate();
        });

        // Deactivate when mouse leaves the map container
        container.addEventListener('mouseleave', () => {
            if (activated) deactivate();
        });
    } else {
        // Touch: first tap shows preview, second activates
        overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!previewing) {
                previewing = true;
                overlay.classList.add('is-previewing');
            } else {
                activate();
            }
        });

        // Deactivate when tapping outside the map
        document.addEventListener('click', (e) => {
            if (activated && !container.contains(e.target)) {
                deactivate();
            }
            if (previewing && !container.contains(e.target)) {
                previewing = false;
                overlay.classList.remove('is-previewing');
            }
        });
    }
}

/**
 * Initialize main map with Leaflet + Stadia Maps
 * If a cached static map image is present, defer tile loading until user activates.
 */
function initializeMainMap(container) {
    const lat = parseFloat(container.dataset.lat);
    const lng = parseFloat(container.dataset.lng);
    const zoom = parseInt(container.dataset.zoom, 10) || 15;

    if (isNaN(lat) || isNaN(lng)) return;

    const staticImg    = container.querySelector('.static-map-image');
    const staticMarker = container.querySelector('.static-map-marker');
    const hasStaticMap  = !!staticImg;

    if (hasStaticMap) {
        setupDeferredMapActivation(container, lat, lng, zoom, staticImg, staticMarker);
    } else {
        initLeafletMap(container, lat, lng, zoom);
    }
}

/**
 * Standard Leaflet map initialisation — extracted from initializeMainMap.
 * Called directly when no static map is present, or after the user activates
 * the map when a static image was being shown.
 */
function initLeafletMap(container, lat, lng, zoom, skipActivationOverlay) {
    map = L.map(container, {
        scrollWheelZoom: false,
        dragging: false,
        touchZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        attributionControl: false
    }).setView([lat, lng], zoom);

    createTileLayer({
        attribution: '&copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'
    }).addTo(map);

    // Fix grey tiles when map container wasn't fully laid out at init (e.g. hard refresh)
    setTimeout(() => map.invalidateSize(), 200);

    // Map activation overlay — skip if called from deferred activation (already activated)
    if (!skipActivationOverlay) {
        setupMapActivation(container, map);
    }

    // Office marker using custom SVG
    const officeIcon = createOfficeIcon();

    const popupTitle = container.dataset.title || '';
    const popupAddress = container.dataset.address || '';
    const popupHtml = popupTitle ? `<strong>${popupTitle}</strong>${popupAddress ? '<br>' + popupAddress : ''}` : '';

    const marker = L.marker([lat, lng], { icon: officeIcon }).addTo(map);
    if (popupHtml) {
        marker.bindPopup(popupHtml);
    }

    // Prepare transport layer (hidden by default)
    transportLayerGroup = L.layerGroup();
    addTransportMarkers(transportLayerGroup, lat, lng);
}

/**
 * Show a static map image with an activation overlay.
 * On click (desktop) or second tap (touch), swap in the live Leaflet map.
 */
function setupDeferredMapActivation(container, lat, lng, zoom, staticImg, staticMarker) {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const overlay = document.createElement('div');
    overlay.className = 'map-activate-overlay';
    overlay.innerHTML = `
        <svg class="map-activate-icon" aria-hidden="true"><use href="/img/sprite-general.svg#icon-map"></use></svg>
        <span>Click to activate map</span>
    `;
    container.appendChild(overlay);

    let activated = false;
    let previewing = false;

    function activate() {
        if (activated) return;
        activated = true;

        staticImg.classList.add('is-fading');
        if (staticMarker) staticMarker.classList.add('is-fading');
        overlay.classList.add('is-hidden');

        // Init Leaflet with interactions already enabled (skip second overlay)
        initLeafletMap(container, lat, lng, zoom, true);

        // Enable all interactions immediately — map is fully active
        map.dragging.enable();
        map.touchZoom.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();
        map.keyboard.enable();
        map.scrollWheelZoom.enable();

        setTimeout(() => {
            staticImg.remove();
            if (staticMarker) staticMarker.remove();
        }, 350);
    }

    if (!isTouch) {
        overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            activate();
        });
    } else {
        overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!previewing) {
                previewing = true;
                overlay.classList.add('is-previewing');
            } else {
                activate();
            }
        });

        document.addEventListener('click', (e) => {
            if (previewing && !container.contains(e.target)) {
                previewing = false;
                overlay.classList.remove('is-previewing');
            }
        });
    }
}

/**
 * Create transport markers from the transport cards on the page
 */
function addTransportMarkers(layerGroup, officeLat, officeLng) {
    document.querySelectorAll('.transport-card').forEach(card => {
        const transportType = card.dataset.transportType;
        const label = card.querySelector('.transport-label')?.textContent || '';

        const icon = L.divIcon({
            className: 'transport-map-marker',
            html: `<div class="marker-transport">${getTransportIcon(transportType)}</div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
            popupAnchor: [0, -22]
        });

        // Primary item coords (on the card itself)
        const cardLat = parseFloat(card.dataset.destLat);
        const cardLng = parseFloat(card.dataset.destLng);
        if (!isNaN(cardLat) && !isNaN(cardLng)) {
            const name = card.querySelector('.transport-name')?.textContent || '';
            const distance = card.querySelector('.transport-distance')?.textContent || '';
            const popupDetail = distance ? `${label} &middot; ${distance}` : label;
            L.marker([cardLat, cardLng], { icon: icon })
                .bindPopup(`<strong>${name}</strong><br>${popupDetail}`)
                .addTo(layerGroup);
        }

        // Sub-item coords (grouped cards with multiple items)
        card.querySelectorAll('.transport-sub-item[data-dest-lat][data-dest-lng]').forEach(item => {
            const itemLat = parseFloat(item.dataset.destLat);
            const itemLng = parseFloat(item.dataset.destLng);
            if (isNaN(itemLat) || isNaN(itemLng)) return;
            // Skip if same coords as primary (avoid duplicate marker)
            if (itemLat === cardLat && itemLng === cardLng) return;

            const itemName = item.querySelector('.transport-sub-name')?.textContent || '';
            const itemDist = item.querySelector('.transport-sub-distance')?.textContent || '';
            const itemPopupDetail = itemDist ? `${label} &middot; ${itemDist}` : label;
            L.marker([itemLat, itemLng], { icon: icon })
                .bindPopup(`<strong>${itemName}</strong><br>${itemPopupDetail}`)
                .addTo(layerGroup);
        });
    });
}

/**
 * Get SVG icon markup for transport type (references external sprite)
 */
function getTransportIcon(type) {
    const icons = {
        airport: 'plane-departure',
        railway: 'train-track',
        subway: 'train-tunnel',
        bus: 'bus',
        ferry: 'ferry',
        tram: 'train-tram'
    };
    const id = icons[type] || 'car-bus';
    return `<svg class="marker-transport-icon" width="18" height="18"><use href="${TRANSPORT_SPRITE_PATH}#${id}"></use></svg>`;
}

function transportBtnLabel(action) {
    const sep = document.querySelector('.office-listing') ? '<br>' : ' ';
    return `<svg class="transport-toggle-icon"><use href="${TRANSPORT_SPRITE_PATH}#car-bus"></use></svg> ${action}${sep}Transport`;
}

/**
 * Toggle transport points layer on/off with smooth zoom
 */
function toggleTransportLayer(btn) {
    if (!map || !transportLayerGroup) return;

    if (transportVisible) {
        // Hide transport markers, restore office markers, reset view
        map.removeLayer(transportLayerGroup);
        if (officeClusterGroup) map.addLayer(officeClusterGroup);
        btn.innerHTML = transportBtnLabel('Show');

        // On listing pages, always restore to initial offices view
        const restoreView = initialListingView || originalView;
        if (restoreView) {
            map.flyTo(restoreView.center, restoreView.zoom, {
                duration: 0.8
            });
        }
    } else {
        // Store current view before zooming out (detail page fallback)
        if (!initialListingView) {
            originalView = {
                center: map.getCenter(),
                zoom: map.getZoom()
            };
        }

        // Swap: remove office markers, add transport markers
        if (officeClusterGroup) map.removeLayer(officeClusterGroup);
        transportLayerGroup.addTo(map);

        // Fit to just the transport markers
        const bounds = L.latLngBounds();
        transportLayerGroup.eachLayer(marker => {
            bounds.extend(marker.getLatLng());
        });

        if (bounds.isValid()) {
            map.flyToBounds(bounds, {
                padding: [40, 40],
                duration: 0.8
            });
        }

        btn.innerHTML = transportBtnLabel('Hide');
    }

    transportVisible = !transportVisible;
}

/**
 * Open Street View dialog
 * Reads the iframe src from the button's data-streetview-src attribute
 * In EE7, this will come from the entry's {street_view_code} field
 */
function openStreetView() {
    const btn = document.querySelector('[data-street-view]');
    const src = btn?.dataset.streetviewSrc;
    if (!src) return;

    loadIframeInDialog('streetview-dialog', src);
}

/**
 * Open mobile full-screen map
 */
function openMobileMap() {
    const dialog = document.getElementById('mobile-map-dialog');
    const container = document.getElementById('mobile-map-container');
    if (!dialog || !container) return;

    openDialog(dialog, {
        onOpen: () => {
            // Small delay to let dialog render before initialising map
            setTimeout(() => {
                if (mobileMap) {
                    mobileMap.invalidateSize();
                    return;
                }

                const mainContainer = document.getElementById('office-map');
                if (!mainContainer) return;

                const lat = parseFloat(mainContainer.dataset.lat);
                const lng = parseFloat(mainContainer.dataset.lng);

                if (isNaN(lat) || isNaN(lng)) return;

                mobileMap = L.map(container, {
                    attributionControl: false
                }).setView([lat, lng], 15);

                createTileLayer().addTo(mobileMap);

                const officeIcon = createOfficeIcon();

                L.marker([lat, lng], { icon: officeIcon }).addTo(mobileMap);
            }, 100);
        }
    });
}

/**
 * Initialize listing page map with clustered markers
 */
function initializeListingMap(container) {
    const lat = parseFloat(container.dataset.lat);
    const lng = parseFloat(container.dataset.lng);
    const zoom = parseInt(container.dataset.zoom, 10) || 11;


    if (isNaN(lat) || isNaN(lng)) return;

    map = L.map(container, {
        scrollWheelZoom: false,
        attributionControl: false
    }).setView([lat, lng], zoom);

    createTileLayer().addTo(map);

    // Gather office markers from cards
    const officeIcon = createOfficeIcon();
    officeClusterGroup = createClusterGroup();
    const cards = document.querySelectorAll('.office-card--listing');
    const bounds = addCardMarkersToGroup(officeClusterGroup, cards, officeIcon);

    map.addLayer(officeClusterGroup);

    if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }

    // Capture initial view after fitBounds settles (for transport toggle reset)
    map.whenReady(() => {
        // Fix grey tiles when map container was not fully laid out at init
        setTimeout(() => map.invalidateSize(), 200);
        initialListingView = {
            center: map.getCenter(),
            zoom: map.getZoom()
        };
    });

    // Prepare transport layer for listing page
    transportLayerGroup = L.layerGroup();
    addTransportMarkers(transportLayerGroup, lat, lng);

    // Transport toggle button
    const transportBtn = document.querySelector('.listing-transport-toggle-btn');
    if (transportBtn) {
        if (transportLayerGroup && transportLayerGroup.getLayers().length > 0) {
            transportBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                toggleTransportLayer(transportBtn);
            });
        } else {
            transportBtn.style.display = 'none';
        }
    }

    // Fullscreen map buttons (sidebar button + quicklink)
    const fullscreenBtn = document.querySelector('.listing-map-fullscreen-btn');
    document.querySelectorAll('[data-map-fullscreen]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            openFullscreenMap(cards);
        });
    });

    // Shrink fullscreen button on any map interaction
    if (fullscreenBtn) {
        const compactify = () => fullscreenBtn.classList.add('listing-map-fullscreen-btn--compact');
        map.on('movestart', compactify);
        map.on('zoomstart', compactify);
    }
}

/**
 * Open fullscreen map dialog with all office markers clustered
 */
function openFullscreenMap(cards) {
    const dialog = document.getElementById('fullscreen-map-dialog');
    const container = document.getElementById('fullscreen-map-container');
    if (!dialog || !container) return;

    openDialog(dialog, {
        onOpen: () => {
            setTimeout(() => {
                if (fullscreenMap) {
                    fullscreenMap.invalidateSize();
                    return;
                }

                fullscreenMap = L.map(container, {
                    attributionControl: false
                });

                createTileLayer().addTo(fullscreenMap);

                const officeIcon = createOfficeIcon();
                const clusterGroup = createClusterGroup();
                const bounds = addCardMarkersToGroup(clusterGroup, cards, officeIcon);

                fullscreenMap.addLayer(clusterGroup);

                if (bounds.isValid()) {
                    fullscreenMap.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
                }
            }, 150);
        }
    });
}

/**
 * Cleanup map instances
 */
function destroyMaps() {
    if (map) {
        map.remove();
        map = null;
    }
    if (mobileMap) {
        mobileMap.remove();
        mobileMap = null;
    }
    if (fullscreenMap) {
        fullscreenMap.remove();
        fullscreenMap = null;
    }
}
